import { CONSENT_VERSIONS, type ConsentMap, type ConsentPurpose } from '@il-colpo/shared/account';
import type { AgeGroup } from '@il-colpo/shared/account';
import { createStore, useStore, type StoreApi } from 'zustand';
import { useProgressStore } from '../state/progressStore.ts';
import { useSettingsStore } from '../state/settingsStore.ts';
import { planForThisPage, type AccountSetup } from './client.ts';
import { CrossSave, forgetSyncMeta, type SyncStatus } from './crossSave.ts';
import {
  SIGNED_OUT,
  fail,
  type AccountClient,
  type AccountSnapshot,
  type AuthErrorCode,
  type Profile,
  type ProfilePatch,
  type Result,
} from './types.ts';

/**
 * What the screens know about the account: whether there is a service, who is signed in, their
 * profile and consents, and how cross-save is doing. The service itself is loaded when the page
 * needs it (`init` for a returning session, `ensure` when an account screen opens), so that a
 * player who never touches accounts never downloads the library.
 */

export interface AccountState {
  setup: AccountSetup;
  client: AccountClient | null;
  /** `idle`: not loaded yet; `loading`; `ready`; `failed`: the service could not be loaded. */
  load: 'idle' | 'loading' | 'ready' | 'failed';
  snapshot: AccountSnapshot;
  profile: Profile | null;
  profileError: AuthErrorCode | null;
  consents: ConsentMap | null;
  /** When the player asked to be deleted: the day it happens. */
  deletion: { scheduledFor: string } | null;
  sync: SyncStatus;

  /** Called once at start-up: loads the service if this page comes back to a session. */
  init: () => void;
  /** Loads the service if it is not loaded, and resolves with it. */
  ensure: () => Promise<AccountClient | null>;
  refreshProfile: () => Promise<void>;
  refreshConsents: () => Promise<void>;
  updateProfile: (patch: ProfilePatch) => Promise<Result<Profile>>;
  declareAge: (group: AgeGroup) => Promise<Result>;
  /** Records an answer for the version of the document in force. */
  answerConsent: (purpose: ConsentPurpose, granted: boolean) => Promise<Result>;
  /** Signs out; `wipeDevice` also leaves nothing of the player's on this device. */
  signOut: (options: { everywhere: boolean; wipeDevice: boolean }) => Promise<Result>;
  requestDeletion: (graceDays: number) => Promise<Result<{ scheduledFor: string | null }>>;
  cancelDeletion: () => Promise<Result<boolean>>;
  syncNow: () => Promise<void>;
}

const IDLE: SyncStatus = { state: 'off', at: null, reason: null };

export function createAccountStore(setup: AccountSetup): StoreApi<AccountState> {
  let crossSave: CrossSave | null = null;
  let loading: Promise<AccountClient | null> | null = null;
  let dataFor: string | null = null;

  const store = createStore<AccountState>((set, get) => {
    const clearData = (): void => {
      dataFor = null;
      set({ profile: null, profileError: null, consents: null, deletion: null });
    };

    /** Reads the player's own data once, when somebody (fully) signs in. */
    const loadData = async (client: AccountClient, userId: string): Promise<void> => {
      if (dataFor === userId) return;
      dataFor = userId;
      await Promise.all([get().refreshProfile(), get().refreshConsents(), loadDeletion(client)]);
    };

    const loadDeletion = async (client: AccountClient): Promise<void> => {
      const r = await client.deletion();
      if (r.ok) set({ deletion: r.value });
    };

    const onSnapshot = (client: AccountClient, snapshot: AccountSnapshot): void => {
      set({ snapshot });
      const user = snapshot.user;
      if (snapshot.status === 'signedIn' && user && !snapshot.needsSecondFactor) {
        void loadData(client, user.id);
      } else if (snapshot.status === 'signedOut') {
        clearData();
        crossSave?.refreshEligibility();
      }
    };

    const attach = async (client: AccountClient): Promise<void> => {
      client.subscribe((snapshot) => {
        onSnapshot(client, snapshot);
      });
      set({ client, snapshot: client.snapshot() });
      await client.start();
      set({ load: 'ready' });
      onSnapshot(client, client.snapshot());

      crossSave = new CrossSave(client, { consents: () => get().consents });
      crossSave.subscribe((sync) => {
        set({ sync });
        if (sync.state === 'ok') void reconcileLanguage(client);
      });
      crossSave.start();
    };

    /** The profile's language follows the setting (the e-mails the service sends are in it). */
    const reconcileLanguage = async (client: AccountClient): Promise<void> => {
      const { profile } = get();
      const language = useSettingsStore.getState().settings.language;
      if (profile && profile.language !== language) {
        const r = await client.updateProfile({ language });
        if (r.ok) set({ profile: r.value });
      }
    };

    return {
      setup,
      client: null,
      load: 'idle',
      snapshot: setup.eager
        ? { status: 'loading', user: null, needsSecondFactor: false }
        : SIGNED_OUT,
      profile: null,
      profileError: null,
      consents: null,
      deletion: null,
      sync: IDLE,

      init: () => {
        if (setup.available && setup.eager) void get().ensure();
      },

      ensure: () => {
        if (!setup.available) return Promise.resolve(null);
        if (loading) return loading;
        set({ load: 'loading' });
        loading = setup
          .load()
          .then(async (client) => {
            await attach(client);
            return client;
          })
          .catch(() => {
            loading = null;
            set({ load: 'failed', snapshot: SIGNED_OUT });
            return null;
          });
        return loading;
      },

      refreshProfile: async () => {
        const client = get().client;
        if (!client) return;
        const r = await client.profile();
        if (r.ok) set({ profile: r.value, profileError: null });
        else set({ profileError: r.error.code });
      },

      refreshConsents: async () => {
        const client = get().client;
        if (!client) return;
        const r = await client.consents();
        if (r.ok) {
          set({ consents: r.value });
          crossSave?.refreshEligibility();
        }
      },

      updateProfile: async (patch) => {
        const client = get().client;
        if (!client) return fail('notConfigured');
        const r = await client.updateProfile(patch);
        if (r.ok) set({ profile: r.value });
        return r;
      },

      declareAge: async (group) => {
        const client = get().client;
        if (!client) return fail('notConfigured');
        const r = await client.declareAge(group);
        if (r.ok) await get().refreshProfile();
        return r;
      },

      answerConsent: async (purpose, granted) => {
        const client = get().client;
        if (!client) return fail('notConfigured');
        const r = await client.recordConsent(purpose, CONSENT_VERSIONS[purpose], granted);
        if (r.ok) await get().refreshConsents();
        return r;
      },

      signOut: async ({ everywhere, wipeDevice }) => {
        const client = get().client;
        if (!client) return fail('notConfigured');
        const r = await client.signOut(everywhere);
        if (r.ok) {
          clearData();
          set({ sync: IDLE });
          if (wipeDevice) {
            useProgressStore.getState().reset();
            // The settings stay (the device is the player's own), but they start a new history.
            forgetSyncMeta();
          }
        }
        return r;
      },

      requestDeletion: async (graceDays) => {
        const client = get().client;
        if (!client) return fail('notConfigured');
        const r = await client.requestDeletion(graceDays);
        if (r.ok) {
          if (graceDays === 0) {
            clearData();
            useProgressStore.getState().reset();
            forgetSyncMeta();
          } else {
            await loadDeletion(client);
          }
        }
        return r;
      },

      cancelDeletion: async () => {
        const client = get().client;
        if (!client) return fail('notConfigured');
        const r = await client.cancelDeletion();
        if (r.ok) set({ deletion: null });
        return r;
      },

      syncNow: async () => {
        await crossSave?.syncNow();
      },
    };
  });
  return store;
}

let instance: StoreApi<AccountState> | null = null;

/** The store of this page, made on first use. */
export function getAccountStore(): StoreApi<AccountState> {
  instance ??= createAccountStore(planForThisPage());
  return instance;
}

/** Tests only: puts a store of their own where the screens look for the page's (null: back to the page's). */
export function setAccountStoreForTests(store: StoreApi<AccountState> | null): void {
  instance = store;
}

export function useAccount<T>(selector: (state: AccountState) => T): T {
  return useStore(getAccountStore(), selector);
}
